/**
 * Canonical date-range presets for the whole platform.
 *
 * Before this module every module invented its own period vocabulary: finance
 * dashboards offered `today | week | month | quarter | year` with no "last
 * month" and no custom range, the reports runner offered a 13-value attendance
 * list, the reports home offered a 14-value list with no custom range at all,
 * and the engagement dashboard offered `30days | 60days | 90days | custom`
 * where "custom" silently resolved to 90 days. Same product, five different
 * option sets and three different controls.
 *
 * Everything date-filtered now resolves through `resolveDateRange` and renders
 * through `<DateRangeFilter />`, so a period means the same thing and looks the
 * same wherever it appears.
 *
 * Legacy keys still arrive from bookmarked URLs, `localStorage` filter state,
 * saved views and scheduled report definitions, so `normalizeDateRangePreset`
 * maps them onto canonical keys instead of silently falling back to a default.
 */

import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { localDateYmd, parseLocalDate } from "@/lib/date-utils";

export type DateRangePresetKey =
  | "all_time"
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_14_days"
  | "last_30_days"
  | "last_60_days"
  | "last_90_days"
  | "last_365_days"
  | "week_to_date"
  | "last_week"
  | "month_to_date"
  | "this_month"
  | "last_month"
  | "quarter_to_date"
  | "this_quarter"
  | "last_quarter"
  | "year_to_date"
  | "this_year"
  | "last_year"
  | "fiscal_year_to_date"
  | "this_fiscal_year"
  | "last_fiscal_year"
  | "custom";

export type DateRangePresetGroup =
  | "Quick"
  | "Week"
  | "Month"
  | "Quarter"
  | "Year"
  | "Fiscal year"
  | "Other";

export interface DateRangePresetOption {
  value: DateRangePresetKey;
  label: string;
  /** Shown as secondary text where the control has room for it. */
  description?: string;
  group: DateRangePresetGroup;
}

/** A concrete, resolved window. `null` means "no date bound" (all time). */
export interface ResolvedDateRange {
  preset: DateRangePresetKey;
  start: Date;
  end: Date;
  label: string;
}

export interface ResolveDateRangeOptions {
  /** Defaults to `new Date()`. Injected by tests and by server-side callers. */
  now?: Date;
  /** 1–12. Comes from `finance_settings.fiscal_year_start_month`. Defaults to January. */
  fiscalYearStartMonth?: number;
  /** `YYYY-MM-DD`, only read for the `custom` preset. */
  customStart?: string | null;
  /** `YYYY-MM-DD`, only read for the `custom` preset. */
  customEnd?: string | null;
  /** 0 = Sunday (the platform default), matching every pre-existing caller. */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

/**
 * Every preset in one ordered list. The order here is the order the control
 * renders, so "the option set" is defined in exactly one place.
 */
export const DATE_RANGE_PRESETS: DateRangePresetOption[] = [
  { value: "today", label: "Today", group: "Quick" },
  { value: "yesterday", label: "Yesterday", group: "Quick" },
  { value: "last_7_days", label: "Last 7 days", group: "Quick" },
  { value: "last_14_days", label: "Last 14 days", group: "Quick" },
  { value: "last_30_days", label: "Last 30 days", group: "Quick" },
  { value: "last_60_days", label: "Last 60 days", group: "Quick" },
  { value: "last_90_days", label: "Last 90 days", group: "Quick" },
  { value: "last_365_days", label: "Last 365 days", group: "Quick" },

  { value: "week_to_date", label: "Week to date", group: "Week" },
  { value: "last_week", label: "Last week", group: "Week" },

  { value: "month_to_date", label: "Month to date", group: "Month" },
  { value: "this_month", label: "This month", description: "Full calendar month", group: "Month" },
  { value: "last_month", label: "Last month", group: "Month" },

  { value: "quarter_to_date", label: "Quarter to date", group: "Quarter" },
  { value: "this_quarter", label: "This quarter", description: "Full calendar quarter", group: "Quarter" },
  { value: "last_quarter", label: "Last quarter", group: "Quarter" },

  { value: "year_to_date", label: "Year to date", group: "Year" },
  { value: "this_year", label: "This year", description: "Full calendar year", group: "Year" },
  { value: "last_year", label: "Last year", group: "Year" },

  { value: "fiscal_year_to_date", label: "Fiscal year to date", group: "Fiscal year" },
  { value: "this_fiscal_year", label: "This fiscal year", group: "Fiscal year" },
  { value: "last_fiscal_year", label: "Last fiscal year", group: "Fiscal year" },

  { value: "custom", label: "Custom range", group: "Other" },
  { value: "all_time", label: "All time", group: "Other" },
];

export const DATE_RANGE_PRESET_GROUP_ORDER: DateRangePresetGroup[] = [
  "Quick",
  "Week",
  "Month",
  "Quarter",
  "Year",
  "Fiscal year",
  "Other",
];

const PRESET_BY_KEY = new Map<DateRangePresetKey, DateRangePresetOption>(
  DATE_RANGE_PRESETS.map((preset) => [preset.value, preset]),
);

/**
 * The default option set. `last_60_days` and `last_365_days` stay out of it so
 * the common dropdown does not run to twenty-plus entries; screens that need
 * them (the engagement dashboard's 60-day default) pass an explicit subset.
 */
export const DEFAULT_DATE_RANGE_PRESETS: DateRangePresetKey[] = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "week_to_date",
  "last_week",
  "month_to_date",
  "this_month",
  "last_month",
  "quarter_to_date",
  "this_quarter",
  "last_quarter",
  "year_to_date",
  "this_year",
  "last_year",
  "fiscal_year_to_date",
  "this_fiscal_year",
  "last_fiscal_year",
  "custom",
  "all_time",
];

/**
 * Legacy keys, by the module that minted them. Bookmarks, saved views,
 * `localStorage` toolbar state and stored scheduled-report filters all still
 * carry these, so they must keep resolving to the window they always meant.
 */
const LEGACY_PRESET_ALIASES: Record<string, DateRangePresetKey> = {
  // finance/DateRangePresets.tsx
  all: "all_time",

  // reports/attendanceDateRange.ts + reports/ReportsHome.tsx
  current_week: "week_to_date",
  current_month: "month_to_date",
  current_quarter: "quarter_to_date",
  qtd: "quarter_to_date",
  ytd: "year_to_date",

  // Contributions.tsx, BatchReport.tsx, useFinanceDashboardData.tsx.
  // These were labelled "This Week"/"This Month"/... in the UI while the query
  // actually ran a trailing window, so they map to the to-date preset the
  // label always promised.
  week: "week_to_date",
  month: "month_to_date",
  quarter: "quarter_to_date",
  year: "year_to_date",

  // dashboard/DateRangeSelector.tsx + hooks/useEngagementData.tsx
  "7days": "last_7_days",
  "14days": "last_14_days",
  "30days": "last_30_days",
  "60days": "last_60_days",
  "90days": "last_90_days",
};

export function isDateRangePresetKey(value: unknown): value is DateRangePresetKey {
  return typeof value === "string" && PRESET_BY_KEY.has(value as DateRangePresetKey);
}

/**
 * Maps any historical or canonical key onto a canonical key. Returns
 * `fallback` for values that were never a preset at all.
 */
export function normalizeDateRangePreset(
  value: string | null | undefined,
  fallback: DateRangePresetKey = "last_30_days",
): DateRangePresetKey {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (isDateRangePresetKey(trimmed)) return trimmed;
  return LEGACY_PRESET_ALIASES[trimmed] ?? fallback;
}

/** The preset options for a screen, in canonical order. */
export function getDateRangePresetOptions(
  keys: readonly DateRangePresetKey[] = DEFAULT_DATE_RANGE_PRESETS,
): DateRangePresetOption[] {
  const wanted = new Set(keys);
  return DATE_RANGE_PRESETS.filter((preset) => wanted.has(preset.value));
}

/** The static label for a preset, ignoring any resolved custom dates. */
export function getDateRangePresetLabel(preset: DateRangePresetKey): string {
  return PRESET_BY_KEY.get(preset)?.label ?? preset;
}

function clampFiscalStartMonth(month: number | undefined): number {
  if (typeof month !== "number" || !Number.isFinite(month)) return 1;
  return Math.min(12, Math.max(1, Math.trunc(month)));
}

/** Start of the fiscal year that `now` falls inside. */
export function getFiscalYearStart(now: Date, fiscalYearStartMonth?: number): Date {
  const month = clampFiscalStartMonth(fiscalYearStartMonth);
  const candidate = startOfDay(new Date(now.getFullYear(), month - 1, 1));
  if (now < candidate) {
    return startOfDay(new Date(now.getFullYear() - 1, month - 1, 1));
  }
  return candidate;
}

/** End of the fiscal year that starts at `fiscalStart` (inclusive). */
function getFiscalYearEnd(fiscalStart: Date): Date {
  const nextStart = new Date(
    fiscalStart.getFullYear() + 1,
    fiscalStart.getMonth(),
    fiscalStart.getDate(),
  );
  return endOfDay(subDays(nextStart, 1));
}

/**
 * True when the org's fiscal year is the calendar year, in which case the
 * fiscal presets duplicate the calendar ones and the control hides them.
 */
export function fiscalYearMatchesCalendarYear(fiscalYearStartMonth?: number): boolean {
  return clampFiscalStartMonth(fiscalYearStartMonth) === 1;
}

const FISCAL_TO_CALENDAR_PRESET: Partial<Record<DateRangePresetKey, DateRangePresetKey>> = {
  fiscal_year_to_date: "year_to_date",
  this_fiscal_year: "this_year",
  last_fiscal_year: "last_year",
};

/**
 * Rewrites a fiscal preset to its calendar twin when the org's fiscal year IS
 * the calendar year. The two resolve to identical windows, so a screen can
 * default to "This fiscal year" without a January-start org seeing a lone
 * "This fiscal year" entry in a dropdown whose fiscal group is hidden.
 */
export function collapseFiscalPreset(
  preset: DateRangePresetKey,
  fiscalYearStartMonth?: number,
): DateRangePresetKey {
  if (!fiscalYearMatchesCalendarYear(fiscalYearStartMonth)) return preset;
  return FISCAL_TO_CALENDAR_PRESET[preset] ?? preset;
}

function formatRangeLabel(start: Date, end: Date): string {
  const fmt = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Resolves a preset into a concrete window.
 *
 * Returns `null` for `all_time` — a null range means "apply no date bound",
 * which callers must distinguish from an unresolvable one. A `custom` preset
 * with missing or malformed dates also returns `null` rather than quietly
 * substituting a different window, so a half-entered custom range shows
 * everything instead of pretending to be the last 90 days (which is what the
 * engagement dashboard used to do).
 */
export function resolveDateRange(
  preset: DateRangePresetKey | string | null | undefined,
  options: ResolveDateRangeOptions = {},
): ResolvedDateRange | null {
  const key = normalizeDateRangePreset(preset);
  const now = options.now ?? new Date();
  const weekStartsOn = options.weekStartsOn ?? 0;
  const label = getDateRangePresetLabel(key);

  const range = (start: Date, end: Date, overrideLabel?: string): ResolvedDateRange => ({
    preset: key,
    start,
    end,
    label: overrideLabel ?? label,
  });

  switch (key) {
    case "all_time":
      return null;

    case "today":
      return range(startOfDay(now), endOfDay(now));

    case "yesterday": {
      const day = subDays(now, 1);
      return range(startOfDay(day), endOfDay(day));
    }

    // Rolling windows are inclusive of today, so "last 7 days" is today plus
    // the six before it.
    case "last_7_days":
      return range(startOfDay(subDays(now, 6)), endOfDay(now));
    case "last_14_days":
      return range(startOfDay(subDays(now, 13)), endOfDay(now));
    case "last_30_days":
      return range(startOfDay(subDays(now, 29)), endOfDay(now));
    case "last_60_days":
      return range(startOfDay(subDays(now, 59)), endOfDay(now));
    case "last_90_days":
      return range(startOfDay(subDays(now, 89)), endOfDay(now));
    case "last_365_days":
      return range(startOfDay(subDays(now, 364)), endOfDay(now));

    case "week_to_date":
      return range(startOfWeek(now, { weekStartsOn }), endOfDay(now));
    case "last_week": {
      const lastWeek = subWeeks(now, 1);
      return range(
        startOfWeek(lastWeek, { weekStartsOn }),
        endOfWeek(lastWeek, { weekStartsOn }),
      );
    }

    case "month_to_date":
      return range(startOfMonth(now), endOfDay(now));
    case "this_month":
      return range(startOfMonth(now), endOfMonth(now));
    case "last_month": {
      const lastMonth = subMonths(now, 1);
      return range(startOfMonth(lastMonth), endOfMonth(lastMonth));
    }

    case "quarter_to_date":
      return range(startOfQuarter(now), endOfDay(now));
    case "this_quarter":
      return range(startOfQuarter(now), endOfQuarter(now));
    case "last_quarter": {
      const lastQuarter = subQuarters(now, 1);
      return range(startOfQuarter(lastQuarter), endOfQuarter(lastQuarter));
    }

    case "year_to_date":
      return range(startOfYear(now), endOfDay(now));
    case "this_year":
      return range(startOfYear(now), endOfYear(now));
    case "last_year": {
      const lastYear = subYears(now, 1);
      return range(startOfYear(lastYear), endOfYear(lastYear));
    }

    case "fiscal_year_to_date":
      return range(getFiscalYearStart(now, options.fiscalYearStartMonth), endOfDay(now));
    case "this_fiscal_year": {
      const start = getFiscalYearStart(now, options.fiscalYearStartMonth);
      return range(start, getFiscalYearEnd(start));
    }
    case "last_fiscal_year": {
      const currentStart = getFiscalYearStart(now, options.fiscalYearStartMonth);
      const start = startOfDay(
        new Date(currentStart.getFullYear() - 1, currentStart.getMonth(), currentStart.getDate()),
      );
      return range(start, endOfDay(subDays(currentStart, 1)));
    }

    case "custom": {
      const start = parseLocalDate(options.customStart);
      const end = parseLocalDate(options.customEnd);
      if (!start || !end) return null;
      // Tolerate a reversed range rather than returning an empty result set.
      const [from, to] = start <= end ? [start, end] : [end, start];
      return range(startOfDay(from), endOfDay(to), formatRangeLabel(from, to));
    }

    default:
      return null;
  }
}

/**
 * Inclusive `YYYY-MM-DD` bounds for a resolved window.
 *
 * Postgres `date` columns (`attendance.event_date`, `contributions.date`, GL
 * posting dates) must be compared against local calendar dates; going through
 * `toISOString()` shifts the boundary by up to a day for users away from UTC.
 */
export function toDateRangeYmdBounds(
  range: ResolvedDateRange | null,
): { startDate: string; endDate: string } | null {
  if (!range) return null;
  return {
    startDate: localDateYmd(range.start),
    endDate: localDateYmd(range.end),
  };
}

/**
 * The label to show for a selection, including the actual dates once a custom
 * range is complete.
 */
export interface DescribeDateRangeOptions extends ResolveDateRangeOptions {
  /**
   * False while the org's fiscal calendar is unknown. The month defaults to
   * January when unknown, and January is exactly the condition for collapsing
   * a fiscal preset onto its calendar twin — so without this a chip or header
   * labels a `this_fiscal_year` selection "This year" on a guess.
   */
  fiscalCalendarResolved?: boolean;
}

export function describeDateRange(
  preset: DateRangePresetKey | string | null | undefined,
  options: DescribeDateRangeOptions = {},
): string {
  const key = normalizeDateRangePreset(preset);
  if (key === "custom") {
    const resolved = resolveDateRange(key, options);
    return resolved ? resolved.label : "Custom range";
  }
  // Collapse so a chip and the control it came from never disagree — one
  // reading "This fiscal year" while the other reads "This year". Not on an
  // unconfirmed month, though: the control does not collapse then either, and
  // the two disagreeing is the whole thing this line exists to prevent.
  if (options.fiscalCalendarResolved === false) return getDateRangePresetLabel(key);
  return getDateRangePresetLabel(collapseFiscalPreset(key, options.fiscalYearStartMonth));
}
